import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../constants/axiosInstance';
import { BatchConfigRow } from '../types/BatchConfigType';
import { trackEvent } from '../../../modules/analytics/trackingMiddleware';
import { BATCH_CONFIG_DELETED } from '../../../modules/analytics/events';

interface DeleteBatchConfigPayload {
  batchId: string;
}
interface ResponseType {
  message: string;
}

const deleteBatchConfig = async (payload: DeleteBatchConfigPayload) => {
  const response = await axiosInstance().post(`/batch/deleteBatch`, payload);
  return response.data;
};

export const useDeleteBatchConfig = (handleClose: () => void, data: BatchConfigRow) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { batchId } = data;

  const { mutate } = useMutation({
    mutationFn: deleteBatchConfig,
    onSuccess: (response: ResponseType) => {
      toast.success(response?.message || t('batchConfig.deleteSuccess', 'Batch Configuration deleted successfully'));
      queryClient.invalidateQueries({ queryKey: ['batchConfigs'] });
      trackEvent(BATCH_CONFIG_DELETED, { batchId, batchName: (data as any)?.batchName || (data as any)?.name || '' });
      handleClose();
    },
    onError: (error: ResponseType) => {
      toast.error(error?.message || t('batchConfig.deleteError', 'Failed to delete Batch Configuration'));
    },
  });

  const handleDelete = useCallback(() => {
    if (batchId) {
      mutate({ batchId });
    } else {
      toast.error(t('batchConfig.idMissing', 'Batch Configuration ID is missing'));
    }
  }, [mutate, batchId]);

  const handleCancel = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const actions = useMemo(
    () => [
      {
        text: t('common.cancel', 'Cancel'),
        handler: handleCancel,
        fullWidth: false,
        color: '#E0E0E0',
        textColor: '#000',
      },
      {
        text: t('common.delete', 'Delete'),
        handler: handleDelete,
        fullWidth: false,
        color: '#FF0000',
        textColor: '#FFF',
      },
    ],
    [handleCancel, handleDelete]
  );

  return {
    handleDelete,
    handleCancel,
    actions,
  };
};
